import {createAsyncThunk, createSlice} from '@reduxjs/toolkit';
import {RequestStatus} from '../../utils/Enums';
import {apiClient} from '../../utils/ApiClient';
import Inquiry from "../../models/Inquiry";
import Order from "../../models/Order";
import {ModifyExtensionFormProps} from "../../components/extensions/ModifyExtensionScreen";
import {NewInquiryFormProps} from "../../components/inquiry/NewInquiryScreen";
import {extractInquirySearchParams} from "../../utils/SearchHelpers";
import {RootState} from "../Store";

interface InquirisState {
    inquiries: Inquiry[];
    currentInquiry: Inquiry | null;
    getInquiriesStatus: RequestStatus;
    getCurrentInquiryStatus: RequestStatus;
    createInquiryStatus: RequestStatus;
    updateInquiryStatus: RequestStatus;
    deleteInquiryStatus: RequestStatus;
    markAnsweredStatus: RequestStatus;
    archiveInquiryStatus: RequestStatus;
    markUnansweredStatus: RequestStatus;
    errors: string[];
    countAnswered: number;
    countUnanswered: number;
    countDrafts: number;
    drafts: Order[];
}

const initialState: InquirisState = {
    inquiries: [],
    currentInquiry: null,
    getInquiriesStatus: RequestStatus.Idle,
    getCurrentInquiryStatus: RequestStatus.Idle,
    createInquiryStatus: RequestStatus.Idle,
    updateInquiryStatus: RequestStatus.Idle,
    deleteInquiryStatus: RequestStatus.Idle,
    markAnsweredStatus: RequestStatus.Idle,
    archiveInquiryStatus: RequestStatus.Idle,
    markUnansweredStatus: RequestStatus.Idle,
    errors: [],
    countAnswered: 0,
    countUnanswered: 0,
    countDrafts: 0,
    drafts: [],
};

export const getInquiries = createAsyncThunk(
    'inquiries/getInquiries',
    async (_, { getState }) => {
        const state = getState() as RootState;
        const userId = state?.info?.me?.id;

        const queryString = extractInquirySearchParams(userId);

        const response = await apiClient.get(`inquiries?${queryString}`);
        return response.data;
    }
);

export const createInquiry = createAsyncThunk('inquiries/createInquiry', async (data: NewInquiryFormProps, {rejectWithValue}) => {
    const response = await apiClient.post('inquiries', data);
    return response.data;
});

export const updateInquiry = createAsyncThunk('inquiries/updateInquiry', async ({id, data}: { id: string | undefined; data: ModifyExtensionFormProps }, {rejectWithValue}) => {
    const response = await apiClient.put(`inquiries/${id}`, data);
    return response.data;
});

export const deleteInquiry = createAsyncThunk('inquiries/deleteInquiry', async (id: number, {rejectWithValue}) => {
    const response = await apiClient.delete(`inquiries/${id}`);
    return response.data;
});

export const getCurrentInquiry = createAsyncThunk('inquiries/getCurrentInquiry', async ({id}: { id: number | undefined }, {rejectWithValue}) => {
    const response = await apiClient.get(`inquiries/${id}`);
    return response.data;
});

export const markAnswered = createAsyncThunk('inquiries/markAnswered', async ({id, data}: { id: string | undefined; data: ModifyExtensionFormProps }, {rejectWithValue}) => {
    const response = await apiClient.put(`inquiries/${id}/mark-answered`, data);
    return response.data;
});

export const archiveInquiry = createAsyncThunk('inquiries/archiveInquiry', async (id: number, {rejectWithValue}) => {
    const response = await apiClient.get(`inquiries/${id}/archive`);
    return response.data;
});

export const setSeen = createAsyncThunk('inquiries/setSeen', async (id: number, {rejectWithValue}) => {
    const response = await apiClient.get(`inquiries/${id}/set-seen`);
    return response.data;
});

export const markUnanswered = createAsyncThunk('inquiries/markUnanswered', async (id: number, {rejectWithValue}) => {
    const response = await apiClient.put(`inquiries/${id}/mark-unanswered`, []);
    return response.data;
});

const inquiriesSlice = createSlice({
    name: 'inquiries',
    initialState,
    reducers: {
        resetCreateInquiryStatus: (state) => {
            state.createInquiryStatus = RequestStatus.Idle;
            state.currentInquiry = null;
        },
        resetUpdateInquiryStatus: (state) => {
            state.updateInquiryStatus = RequestStatus.Idle;
            state.currentInquiry = null;
        },
        resetDeleteInquiryStatus: (state) => {
            state.deleteInquiryStatus = RequestStatus.Idle;
            state.currentInquiry = null;
        },
        resetMarkAnsweredStatus: (state) => {
            state.markAnsweredStatus = RequestStatus.Idle;
            state.currentInquiry = null;
        },
        resetArchiveInquiryStatus: (state) => {
            state.archiveInquiryStatus = RequestStatus.Idle;
            state.currentInquiry = null;
        },
        resetMarkUnansweredStatus: (state) => {
            state.markUnansweredStatus = RequestStatus.Idle;
            state.currentInquiry = null;
        },
    },
    extraReducers: (builder) => {
        builder
            .addCase(getInquiries.pending, (state) => {
                state.getInquiriesStatus = RequestStatus.Loading;
            })
            .addCase(getInquiries.fulfilled, (state, action) => {

                const { inquiries, count, drafts } = action.payload;

                state.inquiries = inquiries;
                state.countAnswered = count.answered;
                state.countUnanswered = count.unanswered;
                state.countDrafts = count.drafts;
                state.drafts = drafts;

                state.getInquiriesStatus = RequestStatus.Success;

            })
            .addCase(getInquiries.rejected, (state) => {
                state.getInquiriesStatus = RequestStatus.Failed;
            })
            .addCase(createInquiry.pending, (state) => {
                state.createInquiryStatus = RequestStatus.Loading;
                state.errors = [];
            })
            .addCase(createInquiry.fulfilled, (state, action) => {
                state.createInquiryStatus = RequestStatus.Success;
                state.currentInquiry = action.payload;
            })
            .addCase(createInquiry.rejected, (state, action) => {
                state.createInquiryStatus = RequestStatus.Failed;
                const errors = JSON.parse(action?.error?.message ?? '{}');
                if (errors) {
                    state.errors = Array.isArray(errors.errors) ? errors.errors : [];
                } else {
                    state.errors = ['Tuvastamata viga.'];
                }
            })
            .addCase(updateInquiry.pending, (state) => {
                state.updateInquiryStatus = RequestStatus.Loading;
                state.errors = [];
            })
            .addCase(updateInquiry.fulfilled, (state) => {
                state.updateInquiryStatus = RequestStatus.Success;
            })
            .addCase(updateInquiry.rejected, (state, action) => {
                state.updateInquiryStatus = RequestStatus.Failed;
                const errors = JSON.parse(action?.error?.message ?? '{}');
                if (errors) {
                    state.errors = Array.isArray(errors.errors) ? errors.errors : [];
                } else {
                    state.errors = ['Tuvastamata viga.'];
                }
            })

            .addCase(deleteInquiry.pending, (state) => {
                state.deleteInquiryStatus = RequestStatus.Loading;
            })
            .addCase(deleteInquiry.fulfilled, (state) => {
                state.deleteInquiryStatus = RequestStatus.Success;
            })
            .addCase(deleteInquiry.rejected, (state) => {
                state.deleteInquiryStatus = RequestStatus.Failed;
            })

            .addCase(getCurrentInquiry.pending, (state) => {
                state.getCurrentInquiryStatus = RequestStatus.Loading;
            })
            .addCase(getCurrentInquiry.fulfilled, (state, action) => {
                state.getCurrentInquiryStatus = RequestStatus.Success;
                state.currentInquiry = action.payload;
            })
            .addCase(getCurrentInquiry.rejected, (state) => {
                state.getCurrentInquiryStatus = RequestStatus.Failed;
            })

            .addCase(markAnswered.pending, (state) => {
                state.markAnsweredStatus = RequestStatus.Loading;
                state.errors = [];
            })
            .addCase(markAnswered.fulfilled, (state) => {
                state.markAnsweredStatus = RequestStatus.Success;
            })
            .addCase(markAnswered.rejected, (state, action) => {
                state.markAnsweredStatus = RequestStatus.Failed;
                const errors = JSON.parse(action?.error?.message ?? '{}');
                if (errors) {
                    state.errors = Array.isArray(errors.errors) ? errors.errors : [];
                } else {
                    state.errors = ['Tuvastamata viga.'];
                }
            })

            .addCase(archiveInquiry.pending, (state) => {
                state.archiveInquiryStatus = RequestStatus.Loading;
            })
            .addCase(archiveInquiry.fulfilled, (state) => {
                state.archiveInquiryStatus = RequestStatus.Success;
            })
            .addCase(archiveInquiry.rejected, (state) => {
                state.archiveInquiryStatus = RequestStatus.Failed;
            })

            .addCase(setSeen.pending, (state) => {

            })
            .addCase(setSeen.fulfilled, (state, action) => {

                const updatedInquiries = state.inquiries.map(inquiry => {
                    if (inquiry.id === action.payload.id) {
                        return {
                            ...inquiry,
                            seen_at: action.payload.seen_at // Assuming action.payload contains { id, seen_at }
                        };
                    }
                    return inquiry;
                });

                state.inquiries = updatedInquiries;

            })
            .addCase(setSeen.rejected, (state) => {

            })

            .addCase(markUnanswered.pending, (state) => {
                state.markUnansweredStatus = RequestStatus.Loading;
            })
            .addCase(markUnanswered.fulfilled, (state) => {
                state.markUnansweredStatus = RequestStatus.Success;
            })
            .addCase(markUnanswered.rejected, (state) => {
                state.markUnansweredStatus = RequestStatus.Failed;
            })
        ;
    },
});

export const {
    resetCreateInquiryStatus,
    resetUpdateInquiryStatus,
    resetDeleteInquiryStatus,
    resetMarkAnsweredStatus,
    resetArchiveInquiryStatus,
    resetMarkUnansweredStatus
} = inquiriesSlice.actions;

export default inquiriesSlice.reducer;